import { useTranslation } from 'react-i18next';

import {
	Alert,
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';

import { currency } from 'lib/helpers';

import useIsMobile from 'hooks/useIsMobile';
import { NfcCardCart } from 'hooks/useNfcCardCart';

type Props = {
	/** The cart, as `useNfcCardCart` holds it. */
	cart: NfcCardCart;
	disabled?: boolean;
	/** Whether loading products can be left out, which adds a "no products" section. */
	optional?: boolean;
	/** Called after another section is picked, which empties the cart. */
	onSectionChange?: () => void;
};

/**
 * Picks the products to load on cards: a section, and how many of each of its products,
 * with their mixer. On a phone each product is a row of its own, with no table to scroll
 * sideways.
 */
export default function NfcCardCartPicker({ cart, disabled, optional, onSectionChange }: Props) {
	const { t } = useTranslation();
	const isMobile = useIsMobile();

	const { sections, section, sectionProducts, productCategoriesMap, lines, search, cart: picked } = cart;

	const visibleProducts = sectionProducts.filter(filterProducts(normalizeText(search)));

	const categoryTitles = (product: Product) =>
		product.categories
			.map((category) => productCategoriesMap[category]?.title)
			.filter(Boolean)
			.join(', ');

	const mixerSelect = (product: Product) => {
		const mixers = product.mixer?.elements ?? [];
		if (mixers.length === 0) return null;

		return (
			<Select
				size="small"
				value={lines[`${product._id}`]?.mixer ?? ''}
				onChange={(event) => cart.updateLine(product, { mixer: event.target.value || null })}
				displayEmpty
				disabled={disabled}
				sx={{ minWidth: 140, flex: isMobile ? 1 : undefined }}
			>
				<MenuItem value="">{product.mixer?.optional ? t('nfc:withoutMixer') : t('nfc:pickMixer')}</MenuItem>
				{mixers.map((mixer) => (
					<MenuItem key={mixer} value={mixer}>
						{mixer}
					</MenuItem>
				))}
			</Select>
		);
	};

	const quantityField = (product: Product) => (
		<TextField
			size="small"
			type="number"
			label={isMobile ? t('nfc:quantity') : undefined}
			value={lines[`${product._id}`]?.quantity ?? 0}
			onChange={(event) =>
				cart.updateLine(product, {
					quantity: Math.max(parseInt(event.target.value, 10) || 0, 0),
				})
			}
			inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
			disabled={disabled}
			sx={{ width: 90, flexShrink: 0 }}
		/>
	);

	return (
		<>
			<FormControl fullWidth sx={{ mb: 2 }}>
				<InputLabel id="nfc-load-section" shrink={optional ? true : undefined}>
					{t('nfc:loadSection')}
				</InputLabel>
				<Select
					labelId="nfc-load-section"
					label={t('nfc:loadSection')}
					value={section?._id ?? ''}
					onChange={(event) => {
						cart.selectSection(event.target.value);
						onSectionChange?.();
					}}
					disabled={disabled}
					displayEmpty={optional}
					notched={optional ? true : undefined}
				>
					{optional && <MenuItem value="">{t('nfc:loadNone')}</MenuItem>}
					{sections
						.filter((element) => !!element._id)
						.map((element) => (
							<MenuItem key={element._id} value={element._id}>
								{element.title}
							</MenuItem>
						))}
				</Select>
			</FormControl>

			{/* The catalogue reads as empty both when there is none and when it could not be read */}
			{!cart.loading && sections.length === 0 && (
				<Typography variant="body2" sx={{ paddingBottom: 2 }}>
					{t('nfc:loadNoSections')}
				</Typography>
			)}

			{!!section && (
				<>
					<TextField
						fullWidth
						value={search}
						label={t('nfc:loadSearch')}
						onChange={(event) => cart.setSearch(event.target.value)}
						sx={{ mb: 1 }}
					/>

					{!cart.loadingProducts && sectionProducts.length === 0 && (
						<Typography variant="body2" sx={{ paddingY: 2 }}>
							{t('nfc:loadNoProducts')}
						</Typography>
					)}

					{isMobile ? (
						<Box>
							{visibleProducts.map((product) => (
								<Box
									key={product._id}
									sx={{
										paddingY: 1.5,
										borderBottom: 1,
										borderColor: 'divider',
										backgroundColor: (lines[`${product._id}`]?.quantity ?? 0) > 0 ? 'action.selected' : undefined,
									}}
								>
									<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
										<Box sx={{ minWidth: 0 }}>
											<Typography variant="body2">{product.title}</Typography>
											<Typography variant="caption">{categoryTitles(product)}</Typography>
										</Box>
										<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
											{currency(product.price ?? 0)}
										</Typography>
									</Box>
									<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, marginTop: 1, justifyContent: 'flex-end' }}>
										{mixerSelect(product)}
										{quantityField(product)}
									</Box>
								</Box>
							))}
						</Box>
					) : (
						<Box sx={{ maxHeight: '45vh', overflowY: 'auto' }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>{t('nfc:product')}</TableCell>
										<TableCell>{t('nfc:price')}</TableCell>
										<TableCell>{t('nfc:mixer')}</TableCell>
										<TableCell>{t('nfc:quantity')}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{visibleProducts.map((product) => (
										<TableRow key={product._id} selected={(lines[`${product._id}`]?.quantity ?? 0) > 0}>
											<TableCell>
												<Typography variant="body2">{product.title}</Typography>
												<Typography variant="caption">{categoryTitles(product)}</Typography>
											</TableCell>
											<TableCell>{currency(product.price ?? 0)}</TableCell>
											<TableCell>{mixerSelect(product) ?? '-'}</TableCell>
											<TableCell>{quantityField(product)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					)}

					{cart.missingMixer && (
						<Alert severity="warning" sx={{ mt: 2 }}>
							{t('nfc:loadMissingMixer')}
						</Alert>
					)}

					{optional && picked.length === 0 && (
						<Typography variant="body2" sx={{ mt: 2 }}>
							{t('nfc:loadNothingPicked')}
						</Typography>
					)}

					<Typography sx={{ mt: 2 }}>{t('nfc:loadEstimatedTotal', { total: currency(cart.total) })}</Typography>
					<Typography variant="caption">{t('nfc:loadTotalHelp')}</Typography>
				</>
			)}
		</>
	);
}

function normalizeText(text: string): string {
	return text
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase();
}

function filterProducts(searchValue: string) {
	return (product: Product) => normalizeText(product.title).includes(searchValue);
}
